<script>
import { PROPOSTAS_LOTE_TEXT } from '@modules/aawz-intelligence-v2/constants/Text.propostas-lote'
import {
  ESTADOS,
  ESTADO_EM_CURSO,
  auxiliarDoEstado,
  permiteCancelar,
  resolverInfoEstado,
  traduzirMotivo,
  formatarContadorPropostas,
  formatarTempoDecorrido,
  formatarCreditos,
  formatarEfeitosConhecidos,
} from './run-status-chip.util'

// Chip de estado do run do Modo Agente (US-13 — spec-002). Sem lógica de
// negócio: quem decide o estado é o store (`runAtual`); este componente só
// renderiza e emite `cancelar`/`tentar-de-novo`. Tabelas/formatadores puros
// vivem em `run-status-chip.util.js` (1.4.9 — mantém este arquivo ≤ 150
// linhas, regra do repo). Detalhes de cada prop/decisão: `CLAUDE.md` do
// módulo §6.
const TICK_ELAPSED_MS = 30000

const ROTULO_CANCELAR = 'Cancelar execução do agente'

export default {
  name: 'IntelligenceV2RunStatusChip',
  props: {
    estado: {
      type: String,
      required: true,
      validator: (v) => Object.hasOwn(ESTADOS, v),
    },
    motivo: { type: String, default: '' },
    podeCancelar: { type: Boolean, default: false },
    propostasAbertas: { type: Number, default: 0 },
    // `custo_creditos` de `GET /runs/{id_run}` — `view-chat.vue` decide
    // QUANDO buscar (entrada em `done`); `null` = ainda não veio.
    custoCreditos: { type: Number, default: null },
    // Já chega gateado pelo pai (`runAtual.retry_permitido` + pós-409).
    podeTentarNovamente: { type: Boolean, default: false },
    carregandoTentativa: { type: Boolean, default: false },
    // [VERIFY-3b/G-D17] `Recibo[]` do evento `estado`/`cancelarRun` — só
    // renderizado em `cancelled` (ver `mostrarEfeitos`).
    efeitosConhecidos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // ★ [lote] "Aprovar todas" mora AQUI, e não no cartão, porque a ação é de
    // THREAD: o chip já anuncia "3 propostas abertas" e é onde o owner
    // espera clicar para decidir as três. Quem decide SE mostra é o pai
    // (`view-chat-lote.mixin.js`: só a partir de duas abertas) — o chip
    // continua sem regra de negócio, igual ao resto das props daqui.
    mostrarAprovarTodas: { type: Boolean, default: false },
    quantidadeParaAprovar: { type: Number, default: 0 },
    aprovandoTodas: { type: Boolean, default: false },
  },
  data() {
    return {
      // Rótulo do "×" (título nativo + nome acessível).
      ROTULO_CANCELAR,
      cancelamentoSolicitado: false,
      // Momento (client clock) em que o front OBSERVOU `running` — evento ao
      // vivo ou snapshot de reidratação (ver util `formatarTempoDecorrido`).
      inicioRunningMs: null,
      tickElapsed: 0,
      intervalElapsedHandle: null,
    }
  },
  computed: {
    infoEstado() {
      return resolverInfoEstado(this.estado)
    },
    rotulo() {
      return this.infoEstado.label
    },
    cor() {
      return this.infoEstado.color
    },
    // [redesenho 19/09/2026] O giro toma o lugar da bolinha — não se soma a
    // ela. A bolinha fica `visibility: hidden` (e não `display: none`) para
    // que o giro caia exatamente no espaço dela: com `display` o rótulo
    // deslizaria 12px a cada entrada/saída de `running`.
    emCurso() {
      return this.estado === ESTADO_EM_CURSO
    },
    auxiliarDoEstadoAtual() {
      return auxiliarDoEstado(this.estado)
    },
    motivoTraduzido() {
      return traduzirMotivo(this.estado, this.motivo)
    },
    mostrarContadorPropostas() {
      return this.estado === 'awaiting_approval' && this.propostasAbertas > 0
    },
    textoContador() {
      return formatarContadorPropostas(this.propostasAbertas)
    },
    // ★ [lote] Rótulo e `aria-label` com o número (o `aria-label` CONTÉM o
    // texto visível — WCAG 2.5.3 "Label in Name", mesma regra que já vale
    // para "Tentar de novo"/"Cancelar" abaixo).
    textoAprovarTodas() {
      return PROPOSTAS_LOTE_TEXT.botao(this.quantidadeParaAprovar)
    },
    ariaAprovarTodas() {
      return PROPOSTAS_LOTE_TEXT.ariaBotao(this.quantidadeParaAprovar)
    },
    mostrarBotaoAprovarTodas() {
      return this.mostrarAprovarTodas && this.quantidadeParaAprovar > 0
    },
    // [redesenho 19/09/2026] O `permiteCancelar` entrou no `&&`: `podeCancelar`
    // diz que HÁ run cancelável (quem sabe é o pai), o estado diz se cancelar
    // ainda significa alguma coisa. Num terminal o "×" seria gesto sem efeito.
    mostrarBotaoCancelar() {
      return (
        this.podeCancelar &&
        permiteCancelar(this.estado) &&
        !this.cancelamentoSolicitado
      )
    },
    tempoDecorridoTexto() {
      // eslint-disable-next-line no-unused-expressions
      this.tickElapsed
      if (this.estado !== 'running') return ''
      return formatarTempoDecorrido(this.inicioRunningMs, Date.now())
    },
    mostrarCreditos() {
      return (
        this.estado === 'done' &&
        this.custoCreditos !== null &&
        this.custoCreditos !== undefined
      )
    },
    textoCreditos() {
      return this.mostrarCreditos ? formatarCreditos(this.custoCreditos) : ''
    },
    mostrarBotaoTentarDeNovo() {
      return this.estado === 'failed' && this.podeTentarNovamente
    },
    // [VERIFY-3b/G-D17] Efeitos já commitados ANTES do cancelamento — US-6
    // pede esse feedback (sem ele, o consultor só vê "cancelado" e não sabe
    // o que já foi gravado).
    mostrarEfeitos() {
      return this.estado === 'cancelled' && this.efeitosConhecidos.length > 0
    },
    textoEfeitos() {
      return this.mostrarEfeitos
        ? formatarEfeitosConhecidos(this.efeitosConhecidos)
        : ''
    },
  },
  watch: {
    estado: {
      immediate: true,
      handler(novo, antigo) {
        if (novo !== 'running') {
          this.pararTimerElapsed()
          this.inicioRunningMs = null
          return
        }
        if (antigo !== 'running') this.inicioRunningMs = Date.now()
        this.iniciarTimerElapsed()
      },
    },
  },
  beforeDestroy() {
    this.pararTimerElapsed()
  },
  methods: {
    onCancelar() {
      if (this.cancelamentoSolicitado) return
      this.cancelamentoSolicitado = true
      this.$emit('cancelar')
    },
    onTentarDeNovo() {
      if (this.carregandoTentativa) return
      this.$emit('tentar-de-novo')
    },
    // ★ [lote] Só avisa o pai — a confirmação e os N POSTs são dele. O guard
    // evita o clique duplo enquanto o laço de decisões está em voo.
    onAprovarTodas() {
      if (this.aprovandoTodas) return
      this.$emit('aprovar-todas')
    },
    iniciarTimerElapsed() {
      if (this.intervalElapsedHandle !== null) return
      this.intervalElapsedHandle = setInterval(() => {
        this.tickElapsed += 1
      }, TICK_ELAPSED_MS)
    },
    pararTimerElapsed() {
      if (this.intervalElapsedHandle === null) return
      clearInterval(this.intervalElapsedHandle)
      this.intervalElapsedHandle = null
    },
  },
}
</script>

<template>
  <output
    class="run-status-chip d-inline-flex align-center pl-4 az-gap-small"
    aria-live="polite"
  >
    <!-- [redesenho 19/09/2026] UM sinal de cor, e ele é a bolinha do próprio
         `BaseStatus soft` (`.status-dot--soft`, que lê `--status-color`). O
         `icon-svg` explícito que ficava aqui SAIU: ele e a bolinha desenhavam
         o mesmo estado duas vezes lado a lado, e a regra do owner é *"o ponto
         de cor substitui o ícone + bolinha duplicados"*. Com ele fora, o CSS
         que escondia a bolinha (`::v-deep .status-dot--soft`) também saiu.
         `soft` (não cru) continua obrigatório: sem ele o rótulo não recebe cor
         no `<span>`, só o ícone — que agora nem existe. -->
    <span
      class="run-status-chip__estado d-inline-flex align-center"
      :class="{
        'run-status-chip__estado--com-cancelar': mostrarBotaoCancelar,
        'run-status-chip__estado--em-curso': emCurso,
      }"
    >
      <BaseStatus soft preserve-case :content="rotulo" :color="cor" />
      <!-- Em `running` a bolinha cede o lugar ao giro. Ela fica
           `visibility: hidden` (style abaixo) e o giro ocupa exatamente a
           mesma caixa — com `display: none` o rótulo saltaria 12px na entrada
           e na saída do estado. -->
      <v-progress-circular
        v-if="emCurso"
        class="run-status-chip__giro"
        indeterminate
        :size="10"
        :width="2"
        :color="cor"
        aria-hidden="true"
      />
      <!-- O "×" mora DENTRO da pílula (pedido do usuário, 17/09): o `BaseStatus`
           não tem slot, então ele é absoluto sobre a folga aberta à direita.
           [19/09] Só em `running`/`awaiting_approval` — ver `mostrarBotaoCancelar`. -->
      <button
        v-if="mostrarBotaoCancelar"
        type="button"
        class="run-status-chip__cancelar d-inline-flex align-center justify-center"
        :title="ROTULO_CANCELAR"
        :aria-label="ROTULO_CANCELAR"
        @click="onCancelar"
      >
        <icon-svg name="close-circle" :size="12" color="var(--neutral-700)" />
      </button>
    </span>
    <!-- Auxiliar FIXO do estado (hoje só `queued`: "Aguardando um agente
         livre"). Os demais auxiliares abaixo são dado do run. -->
    <span
      v-if="auxiliarDoEstadoAtual"
      class="run-status-chip__auxiliar az-text-secondary az-size-12 az-muted"
    >
      {{ auxiliarDoEstadoAtual }}
    </span>
    <span
      v-if="mostrarContadorPropostas"
      class="run-status-chip__contador az-text-secondary az-size-12 az-muted"
    >
      {{ textoContador }}
    </span>
    <span
      v-if="motivoTraduzido"
      class="run-status-chip__motivo az-text-secondary az-size-12 az-muted"
    >
      {{ motivoTraduzido }}
    </span>
    <span
      v-if="tempoDecorridoTexto"
      class="run-status-chip__elapsed az-text-secondary az-size-12 az-muted"
    >
      {{ tempoDecorridoTexto }}
    </span>
    <span
      v-if="mostrarCreditos"
      class="run-status-chip__creditos az-text-secondary az-size-12 az-muted"
    >
      {{ textoCreditos }}
    </span>
    <span
      v-if="mostrarEfeitos"
      class="run-status-chip__efeitos az-text-secondary az-size-12 az-muted"
    >
      {{ textoEfeitos }}
    </span>
    <!-- [VERIFY-3h, Q-8 qa.json it.3] `aria-label` tem que CONTER o texto
         visível (comando de voz/WCAG 2.5.3 "Label in Name") — antes era
         "Tentar executar o agente de novo", que não contém "Tentar de novo"
         como substring contígua. Igual ao texto visível, mesmo padrão do
         botão Cancelar logo abaixo (que já estava certo: "Cancelar execução
         do agente" começa com o texto visível "Cancelar"). -->
    <BaseButton
      v-if="mostrarBotaoTentarDeNovo"
      underlined
      sm
      fitContent
      :loading="carregandoTentativa"
      :disabled="carregandoTentativa"
      aria-label="Tentar de novo"
      @click="onTentarDeNovo"
      >Tentar de novo</BaseButton
    >
    <!-- ★ [lote] Ação de THREAD, ao lado do contador que ela resolve. É a
         única ação PRIMÁRIA do chip (as outras são `underlined`): é o que o
         consultor veio fazer quando há fila de propostas — o resto do chip é
         estado. `sm fitContent` mantém a altura da linha do chip; `small` é
         reservada a CTA inline de select/input. -->
    <BaseButton
      v-if="mostrarBotaoAprovarTodas"
      primary
      sm
      fitContent
      :loading="aprovandoTodas"
      :disabled="aprovandoTodas"
      :aria-label="ariaAprovarTodas"
      @click="onAprovarTodas"
    >
      {{ textoAprovarTodas }}
    </BaseButton>
  </output>
</template>

<style lang="sass" scoped>
.run-status-chip__estado
  position: relative

.run-status-chip__estado--com-cancelar ::v-deep .status-chip--soft
  padding-right: 22px

// [redesenho 19/09/2026] A bolinha fica no lugar (ocupando espaço) e o giro
// entra por cima dela. `visibility` e não `display`: com `display: none` a
// linha inteira do rótulo desliza 12px ao entrar e ao sair de `running`.
.run-status-chip__estado--em-curso ::v-deep .status-dot--soft
  visibility: hidden

// Ancorado na MESMA caixa da bolinha: `.status-chip--soft` tem `padding: 4px
// 8px` e a bolinha 8px de lado, então `left: 7px` centra os 10px do giro sobre
// ela. `pointer-events: none` porque o chip inteiro é `<output>` — o giro não
// pode roubar o clique do "×" ao lado.
.run-status-chip__giro
  position: absolute
  left: 7px
  top: 50%
  transform: translateY(-50%)
  pointer-events: none

.run-status-chip__cancelar
  position: absolute
  right: 6px
  top: 50%
  transform: translateY(-50%)
  opacity: 0.85
  padding: 0
  border: none
  background: none
  cursor: pointer
  line-height: 0

  &:hover
    opacity: 1

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px
    border-radius: var(--radius-sm)

.run-status-chip
  // Respiro à esquerda: a linha nascia colada no botão de recolher a lateral.
  padding-left: 16px

// ⚠️ **O bloco que ESCONDIA a bolinha saiu em 19/09/2026.** Entre o VERIFY-3f
// e esta data, o chip desenhava um `icon-svg` explícito E a bolinha do
// `BaseStatus soft`, e a bolinha era apagada aqui (`::v-deep
// .status-dot--soft { display: none }`) para o estado não aparecer duas vezes.
// O redesenho do owner inverteu a escolha: quem fica é a BOLINHA (ela já nasce
// na cor do estado, sem prop nenhuma), e quem saiu é o ícone. Um `display:
// none` esquecido aqui deixaria o chip sem sinal de cor nenhum — e sem erro,
// porque o rótulo continuaria colorido.
</style>
